import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Clock, Truck, Leaf, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SectionHeading from '../components/SectionHeading';
import { useMenu } from '@happyspace/sdk/restaurant';
import MenuItemCard from '../components/MenuItemCard';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';

const BRAND_BANNER = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/485ee0f5-162d-499f-b44f-69bc80fbabf9/1785626036737-vd8ge.png';
const HERO_IMG = BRAND_BANNER;
const ABOUT_IMG = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-08-01/8a8598f2b264.png';
const GALLERY_IMG = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-08-01/29603b24278d.png';

const PILLARS = [
  { icon: Flame, title: 'Dum-Sealed & Slow-Cooked', copy: 'Every handi is sealed with dough and steamed low and slow — the way real biryani is meant to be made.' },
  { icon: Clock, title: 'Ready When You Are', copy: 'Order ahead for pickup, or have it delivered hot to your door.' },
  { icon: Leaf, title: 'Veg & Non-Veg, Equally Serious', copy: 'Our vegetarian dabbas get the same layering and patience as our meat biryanis. No afterthoughts here.' },
];

export default function Home() {
  const { items, loading } = useMenu();
  const { addItem } = useRestaurantCart();
  const featured = items.slice(0, 3);

  return (
    <>
      <Helmet>
        <title>Dum-Cooked Biryani Omaha | Biryani2Go Takeout & Delivery Open Now</title>
        <meta name="description" content="Dum-cooked biryani in Omaha & Council Bluffs — sealed, steamed, delivered hot. Order now for pickup or delivery. Restaurant near me open 11 AM–10 PM daily." />
        <meta name="keywords" content="restaurant near me, restaurant near me open now, restaurant near me open, restaurant near me takeout, restaurant near me within 0.2 mi, biryani omaha, biryani council bluffs, Indian restaurant omaha, takeout near me" />
        <link rel="canonical" href="https://biryani2go.us/" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Biryani2Go — Dum-Cooked Biryani Omaha",
            "description": "Fresh dum-cooked biryani in Omaha & Council Bluffs. Order now for pickup or delivery.",
            "url": "https://biryani2go.us",
            "publisher": {
              "@type": "LocalBusiness",
              "name": "Biryani2Go",
              "image": "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/485ee0f5-162d-499f-b44f-69bc80fbabf9/1785626036737-vd8ge.png"
            }
          }
        ` }} />
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-black">
          <img src={HERO_IMG} alt="Steaming dum biryani in a copper handi" className="w-full h-full object-contain opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
        </div>

        <div className="relative max-w-screen-xl mx-auto px-5 md:px-8 pt-28 pb-32 md:pt-40 md:pb-44">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow text-marigold mb-5"
          >
            Sealed. Steamed. Served Hot.
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-semibold text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-[-0.02em] text-white max-w-3xl"
          >
            Dum-Cooked Biryani, Sealed & Steamed to Order
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="mt-6 text-lg md:text-xl leading-relaxed text-[#F0DCC2] max-w-[52ch]"
          >
            No shortcuts, no microwave rice. Just fragrant basmati, hand-layered meat or vegetables,
            and a dough seal that locks in every bit of steam — packed to go, straight to your table.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.34 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link to="/menu" className="btn-primary text-base">
              Order Your Dabba <ArrowRight size={18} />
            </Link>
            <Link to="/about" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white border-2 border-white/40 hover:bg-white/10 transition-colors">
              Our Story
            </Link>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-surface py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="card-tin p-8"
              >
                <div className="w-12 h-12 rounded-full bg-ember/10 flex items-center justify-center mb-5">
                  <p.icon size={22} className="text-ember" />
                </div>
                <h3 className="font-display font-semibold text-xl text-maroon mb-2">{p.title}</h3>
                <p className="text-sm leading-relaxed text-ink-2">{p.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED MENU */}
      <section className="bg-surface2 py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow="From the Handi"
              title="Signature Biryanis"
              description="A taste of what's steaming right now. Full menu has more — combos, sides, and desserts."
            />
            <Link to="/menu" className="hidden md:inline-flex items-center gap-2 font-semibold text-ember hover:gap-3 transition-all whitespace-nowrap">
              View Full Menu <ArrowRight size={18} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="card-tin h-96 animate-pulse bg-surface" />
              ))}
            </div>
          ) : featured.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featured.map((item, i) => (
                <MenuItemCard key={item._id} item={item} onAdd={(it) => addItem(it)} index={i} />
              ))}
            </div>
          ) : (
            <div className="card-tin p-12 text-center">
              <p className="font-display text-2xl text-maroon mb-2">Menu coming soon</p>
              <p className="text-ink-2 mb-6">We're plating up the details. Check back shortly, or reach out directly.</p>
              <Link to="/contact" className="btn-secondary">Contact Us</Link>
            </div>
          )}

          <Link to="/menu" className="md:hidden mt-8 inline-flex items-center gap-2 font-semibold text-ember">
            View Full Menu <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* STORY STRIP */}
      <section className="bg-surface py-20 md:py-28 overflow-hidden">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="order-2 md:order-1"
          >
            <p className="eyebrow mb-3">Our Craft</p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl leading-tight tracking-[-0.01em] text-maroon">
              Every handi gets its own seal.
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-ink-2 max-w-[56ch]">
              Dum cooking means sealing the pot with dough and letting the rice, meat, and spices steam
              together for hours — no stirring, no shortcuts. It's slower than fast food, and that's the
              entire point.
            </p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 font-semibold text-ember hover:gap-3 transition-all">
              Read our story <ArrowRight size={18} />
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="order-1 md:order-2 relative"
          >
            <div className="rounded-[2rem] overflow-hidden card-tin aspect-[4/5]">
              <img src={ABOUT_IMG} alt="Sealed copper biryani handi with steam rising" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full bg-marigold flex items-center justify-center text-maroon font-display font-bold text-center leading-tight text-sm p-3 shadow-xl animate-spin-slow">
              slow · sealed · steamed
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION — Restaurant Near Me Queries */}
      <section className="bg-surface py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          <SectionHeading
            eyebrow="Common Questions"
            title="Restaurant Near Me? We're Here in Omaha & Council Bluffs"
            description="Real answers to the questions we hear most from folks hunting for biryani, dosa, or samosa near their area."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="card-tin p-6 md:p-8"
            >
              <h3 className="font-display font-semibold text-lg text-maroon mb-3">Is Biryani2Go open now?</h3>
              <p className="text-sm md:text-base leading-relaxed text-ink-2">
                Yes. We're open <strong>11 AM to 10 PM daily</strong> in Omaha & Council Bluffs for pickup and delivery. Check the menu page to start your order anytime.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="card-tin p-6 md:p-8"
            >
              <h3 className="font-display font-semibold text-lg text-maroon mb-3">Restaurant near me — do you deliver?</h3>
              <p className="text-sm md:text-base leading-relaxed text-ink-2">
                We deliver across Omaha and Council Bluffs. Orders arrive hot & sealed. Delivery typically takes 15–30 minutes depending on distance.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="card-tin p-6 md:p-8"
            >
              <h3 className="font-display font-semibold text-lg text-maroon mb-3">What about dine-in or takeout?</h3>
              <p className="text-sm md:text-base leading-relaxed text-ink-2">
                We specialize in <strong>takeout & delivery</strong> for the freshest, hottest dum-cooked biryani straight to your door or ready for quick pickup.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="card-tin p-6 md:p-8"
            >
              <h3 className="font-display font-semibold text-lg text-maroon mb-3">What should I order?</h3>
              <p className="text-sm md:text-base leading-relaxed text-ink-2">
                Start with a signature biryani—<strong>veg, chicken, mutton, or goat</strong>. First time? Read our <Link to="/blog" className="text-ember font-semibold underline">what-to-order guide</Link>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GALLERY TEASER + CTA */}
      <section className="relative bg-maroon py-20 md:py-28 overflow-hidden">
        <img src={GALLERY_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-screen-xl mx-auto px-5 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="eyebrow text-marigold mb-4">Pickup or Delivery</p>
            <h2 className="font-display font-semibold text-3xl md:text-5xl text-white max-w-2xl mx-auto leading-tight">
              Your dabba is waiting. Let's get it to you.
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/menu" className="btn-primary text-base">
                Start Your Order <ArrowRight size={18} />
              </Link>
              <Link to="/gallery" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white border-2 border-white/40 hover:bg-white/10 transition-colors">
                See the Gallery
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
